import { getCurrentScope, onScopeDispose, ref, shallowRef, toValue, watch, type MaybeRefOrGetter, type Ref, type ShallowRef } from 'vue';
import { ApiError } from '../api.ts';
import type { LiveClient } from './client.ts';
import { liveClient } from './index.ts';

// Live data for components (design §6.5): `useLive` for messages, and
// `useLiveResource` for the usual pattern — subscribe, fetch a snapshot, apply live
// changes over it, fetch again on resync.

function forbidden(reason: string): ApiError {
  return new ApiError(403, 'forbidden', reason === 'unknown' ? 'There is no such live topic' : "You don't have access to this");
}

function onDispose(fn: () => void): void {
  if (getCurrentScope() !== undefined) onScopeDispose(fn);
}

/**
 * Calls `onMessage` for each message on `topic` while the calling component (or
 * effect scope) lives. A reactive topic resubscribes when it changes; null subscribes to nothing.
 */
export function useLive(topic: MaybeRefOrGetter<string | null>, onMessage: (data: unknown) => void, options: { client?: LiveClient; onRevoked?: (reason: string) => void; onResync?: () => void } = {}): void {
  const client = options.client ?? liveClient();
  let off: (() => void) | undefined;
  watch(
    () => toValue(topic),
    (next) => {
      off?.();
      off = next === null ? undefined : client.subscribe(next, { onMessage, ...(options.onRevoked ? { onRevoked: options.onRevoked } : {}), ...(options.onResync ? { onResync: options.onResync } : {}) });
    },
    { immediate: true },
  );
  onDispose(() => {
    off?.();
  });
}

export interface LiveResource<T> {
  /** The snapshot with live changes applied; undefined until the first fetch succeeds. */
  data: ShallowRef<T | undefined>;
  loading: Ref<boolean>;
  /** The last fetch's error, or a 403 once the topic was refused or taken away. */
  error: ShallowRef<unknown>;
  /** Fetches the snapshot again. */
  refresh: () => Promise<void>;
}

/**
 * Subscribes to `topic`, waits until the server has the subscription, then fetches.
 * Messages that arrive before the snapshot are buffered and applied after it, in
 * order, so none is lost or applied to stale data. A message may describe a change
 * the snapshot already has, so `apply` must be idempotent (upsert by id, not append).
 * `apply(current, message)` returns the next value; without `apply`, every message
 * fetches again (for invalidation-only topics such as `core.groups`), including one
 * that arrived while a snapshot was loading. A `resync` fetches again.
 */
export function useLiveResource<T>(
  fetch: () => Promise<T>,
  topic: MaybeRefOrGetter<string | null>,
  apply?: (current: T, message: unknown) => T,
  options: { client?: LiveClient } = {},
): LiveResource<T> {
  const data = shallowRef<T | undefined>();
  const loading = ref(false);
  const error = shallowRef<unknown>();
  let buffer: unknown[] | null = null;
  let generation = 0;

  const client = options.client ?? liveClient();

  async function refresh(): Promise<void> {
    const mine = ++generation;
    buffer = [];
    loading.value = true;
    try {
      const current = toValue(topic);
      const granted = current === null || (await client.whenSubscribed(current));
      if (mine !== generation) return;
      if (!granted) {
        buffer = null;
        error.value = forbidden('forbidden');
        return;
      }
      const snapshot = await fetch();
      if (mine !== generation) return;
      const pending = buffer;
      buffer = null;
      data.value = apply === undefined ? snapshot : pending.reduce<T>((current, message) => apply(current, message), snapshot);
      error.value = undefined;
      // Without apply, a message means "fetch again": one that arrived while this snapshot loaded may describe a change it missed.
      if (apply === undefined && pending.length > 0) void refresh();
    } catch (failure) {
      if (mine !== generation) return;
      buffer = null;
      error.value = failure;
    } finally {
      if (mine === generation) loading.value = false;
    }
  }

  const onMessage = (message: unknown): void => {
    if (buffer !== null) {
      buffer.push(message);
      return;
    }
    if (apply === undefined) {
      void refresh();
      return;
    }
    if (data.value !== undefined) data.value = apply(data.value, message);
  };

  useLive(topic, onMessage, {
    client,
    onRevoked: (reason) => {
      error.value = forbidden(reason);
    },
    onResync: () => {
      void refresh();
    },
  });
  // Subscribed first (above), so nothing published while the snapshot loads is missed.
  watch(
    () => toValue(topic),
    () => {
      void refresh();
    },
    { immediate: true },
  );
  return { data, loading, error, refresh };
}
